import converter from '../../utils/converter';
import pairingService from '../pairingService';
import uamClientService from '../uam/uamClientService';
import cmMessageRequestService from './cmMessageRequestService';
import cmMessageResponseService from './cmMessageResponseService';
import {selectIsConnectedToCm} from '../../state/selectors/iotData';
import cmTimeouts from '../../consts/communicationLayer/cmTimeouts';
import cmService from './cmService';
import {setConnectedToCm} from '../../state/slices/iotData';
import {log} from '../log';
import {selectUamAssetId} from '../../state/selectors/uamData';
import uamFlowService from '../uam/uamFlowService';
import * as uamFlowTypes from '../../consts/uam/uamFlowTypes';
import {onWebSocketDisconnect} from '../device/connectionService';

const initCmClient = async (isHolder) => {
    checkDeviceConnected();

    if (isCmClientConnected(isHolder)) {
        const isConnected = selectIsConnectedToCm(isHolder);

        if (!isConnected) {
            cmMessageRequestService.publishInitializationEvent(isHolder);
        }

        return;
    }

    log.info(`Connect to CM is started. isHolder: ${isHolder}`);

    await connectToCmClient(isHolder);
};

const checkDeviceConnected = () => {
    if (pairingService.isDeviceConnected()) return;

    throw new Error('Device is not connected');
};

const connectToCmClient = async (isHolder) => {
    const onError = () => {
        onWebSocketDisconnect(isHolder);
    };

    const cmClient = cmService.getCmClient(isHolder)();

    if (cmClient) {
        await cmClient.reconnectClient(async () => {
            //init new connection if reconnect can not be done
            await initNewCmConnection(isHolder, onError);
        });
        return;
    }

    await initNewCmConnection(isHolder, onError);
};

const initNewCmConnection = async (isHolder, onError) => {
    const assetId = selectUamAssetId(isHolder);

    if (!assetId) throw new Error('AssetId is not provided');

    const cmSettings = await uamClientService.connectDevice(assetId, isHolder);

    if (!cmSettings) throw new Error('CM settings is not provided');

    checkDeviceConnected();

    const cmClient = cmService.getCmClient(isHolder)(assetId, cmSettings);

    if (!cmClient) throw new Error('CM client is not initialized');

    const onCloseHandler = () => {
        setConnectedToCm(isHolder, false);
    };

    const onConnectHandler = () => {
        subscribeOnMessage(isHolder);
    };

    cmClient.attachHandlers(onConnectHandler, onCloseHandler, onError);
    cmClient.connect();

    uamFlowService.createFlow({
        type: uamFlowTypes.CONNECT,
        timeoutInSec: cmTimeouts.CM_CONNECT_TIMEOUT,
        isHolder,
    });
};

const disconnectCmClient = (isHolder, isClearReconnectData = true) => {
    let cmClient;

    const disconnectClient = (isHolder) => {
        cmClient = cmService.getCmClient(isHolder)();
        if (cmClient) {
            if (isClearReconnectData) {
                cmMessageRequestService.publishDisconnectEvent(isHolder);
                cmClient.disconnectAndClear();
            } else {
                cmClient.disconnect();
            }
        }
    };

    if (isHolder === undefined) {
        disconnectClient(false);
        disconnectClient(true);
    } else {
        disconnectClient(isHolder);
    }
};

const maskTokenInPayload = (payloadString) => {
    try {
        const payload = JSON.parse(payloadString);
        if (payload.token) {
            const {token} = payload;
            // Show first 3 and last 3 characters, mask the rest
            payload.token = `${token.slice(0, 3)}...${token.slice(-3)}`;
        }
        return JSON.stringify(payload);
    } catch (e) {
        return payloadString;
    }
};

const subscribeOnMessage = (isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return;

    cmClient.subscribe();

    cmClient.attachMessageHandler((message) => {
        const {payloadString} = message;

        log.info(`CmClient: isHolder: ${isHolder} new message: ${maskTokenInPayload(payloadString)}`);
        const mes = converter.stringToJson(payloadString);

        cmMessageResponseService.onCmMessage(mes, isHolder);
    });

    cmMessageRequestService.publishInitializationEvent(isHolder);
};

const publishMessage = (message, isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return;

    cmClient.publish(message);
};

const isCmClientConnected = (isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return false;

    const assetId = selectUamAssetId(isHolder);

    return !!cmClient.isConnected(assetId);
};

export default {
    initCmClient,
    publishMessage,
    disconnectCmClient,
    isCmClientConnected,
};
